/**
 * SD-10682: the password reset email links to `/auth/password/reset;token=...;email=...`.
 * Stripe and Plaid are synchronous <script> tags in the document head, and Google Tag Manager
 * is injected at app boot (GtmService constructor); all of them read `document.location`, so
 * the token has to leave the address bar before any of them run. An inline script at the top
 * of src/index.html does the rewrite and parks the original path on `window.__payhoaResetPath`;
 * `stripSensitiveRouteParams()` (called from main.ts) parses that path, keeps the values in
 * memory for the reset component, and also handles the URL itself if the inline script did not
 * run (tests, a future index.html change).
 *
 * In-memory on purpose: sessionStorage would keep a live reset token in browser storage.
 * A hard refresh after the rewrite therefore lands on request-reset-email with no-token,
 * which is the same outcome as opening the reset route without a link.
 */

declare global {
    interface Window {
        __payhoaResetPath?: string;
    }
}

export const RESET_PASSWORD_PATH = '/auth/password/reset';

export interface StashedResetParams {
    token: string | null;
    email: string | null;
}

let stashedResetParams: StashedResetParams | null = null;

/**
 * Parse Angular matrix params (`;key=value`) off a pathname segment.
 */
export function parseMatrixParams(pathname: string): { path: string; params: Record<string, string> } {
    const [path, ...pairs] = pathname.split(';');
    const params: Record<string, string> = {};
    for (const pair of pairs) {
        const eq = pair.indexOf('=');
        if (eq === -1) {
            continue;
        }
        const key = pair.slice(0, eq);
        let value = pair.slice(eq + 1);
        try {
            value = decodeURIComponent(value);
        } catch {
            // leave the raw value if it is not valid percent-encoding
        }
        params[key] = value;
    }
    return { path, params };
}

/**
 * If the page was opened on the reset route with matrix params, stash the token and email in
 * memory and make sure the address bar shows the bare route. Reads the original path parked by
 * the inline script in index.html when present, otherwise the live location. Returns the stash
 * (or null when this was not a reset link). Safe to call on every page load.
 */
export function stripSensitiveRouteParams(
        loc: { pathname: string; search: string; hash: string } = window.location,
        hist: { replaceState: (data: unknown, unused: string, url: string) => void } = window.history,
        win: { __payhoaResetPath?: string } = window,
): StashedResetParams | null {
    const originalPath = win.__payhoaResetPath || loc.pathname;
    delete win.__payhoaResetPath;

    const { path, params } = parseMatrixParams(originalPath);
    if (path !== RESET_PASSWORD_PATH || (!('token' in params) && !('email' in params))) {
        return null;
    }

    stashedResetParams = {
        token: params['token'] || null,
        email: params['email'] || null,
    };
    if (loc.pathname !== RESET_PASSWORD_PATH) {
        hist.replaceState(null, '', RESET_PASSWORD_PATH + (loc.search || '') + (loc.hash || ''));
    }
    return stashedResetParams;
}

/**
 * Hand the stashed values to the reset component once, then forget them.
 */
export function consumeStashedResetParams(): StashedResetParams {
    const stash = stashedResetParams || { token: null, email: null };
    stashedResetParams = null;
    return stash;
}

/** Test hook. */
export function clearStashedResetParams(): void {
    stashedResetParams = null;
}
